import axios, { AxiosError } from 'axios'
import type { LoginRequest, LoginResponse, UserConfig, ApiError, ResultsResponse, SessionStatus } from '../types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', credentials)
    return response.data
  },

  logout: () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  },
}

export const userApi = {
  getConfig: async (): Promise<UserConfig> => {
    const response = await api.get<{ config: UserConfig }>('/user/config')
    return response.data.config
  },

  updateDefaultPrompt: async (prompt: string): Promise<void> => {
    await api.put('/user/config/default-prompt', { prompt })
  },

  addAlternativePrompt: async (prompt: string): Promise<void> => {
    await api.post('/user/config/alternative-prompts', { prompt })
  },

  updateAlternativePrompt: async (id: string, prompt: string): Promise<void> => {
    await api.put(`/user/config/alternative-prompts/${id}`, { prompt })
  },

  deleteAlternativePrompt: async (id: string): Promise<void> => {
    await api.delete(`/user/config/alternative-prompts/${id}`)
  },

  updateMultiScreenshotPrompt: async (prompt: string): Promise<void> => {
    await api.put('/user/config/multi-screenshot-prompt', { prompt })
  },

  getResults: async (page: number = 1, limit: number = 10): Promise<ResultsResponse> => {
    const response = await api.get<ResultsResponse>(`/results?page=${page}&limit=${limit}`)
    return response.data
  },
}

export const sessionApi = {
  getStatus: async (): Promise<SessionStatus> => {
    const response = await api.get<SessionStatus>('/sessions/status')
    return response.data
  },
}

export default api